/* eslint-disable no-console */
import { defineComponent } from 'vue';
import {
  ICON_TEXT_BUTTON,
  ICON_BUTTON,
  TEXT_BUTTON,
  BEFORE_ICON_POSITION,
  AFTER_ICON_POSITION,
  BUTTON_TYPE_LARGE,
  BUTTON_TYPE_MEDIUM,
  BUTTON_TYPE_SMALL,
  DEFAULT_COLOR,
  DISPLAY_TITLE_BUTTON,
  IS_SETTING_ICON_IN_BUTTON,
} from '@modules/editor-sidebar';
import { mapGetters, mapState } from 'vuex';
import { getPackAttrByKey } from '@modules/global-style';

export default defineComponent({
  data() {
    return {
      ICON_TEXT_BUTTON,
      ICON_BUTTON,
      TEXT_BUTTON,
      BEFORE_ICON_POSITION,
      AFTER_ICON_POSITION,
      BUTTON_TYPE_LARGE,
      BUTTON_TYPE_MEDIUM,
      BUTTON_TYPE_SMALL,
      IS_SETTING_ICON_IN_BUTTON,
    };
  },
  computed: {
    ...mapGetters('sidebar', {
      buttons: 'getCurrentPresetButtons',
      color: 'getCurrentPresetColor',
      presetBorders: 'getCurrentPresetBorders',
      presetTexts: 'getCurrentPresetTexts',
      presetConners: 'getCurrentPresetCorners',
    }),
    ...mapState('sidebar', {
      currentPreset: (state: any) => state.currentPreset,
    }),
    presetColors() {
      const colorsValue = this.color.value;
      let presetColors: any = [];
      for (const colorType in colorsValue) {
        for (const color in Object(colorsValue)[colorType]) {
          const className = `${colorType}-${color}`;
          if (colorsValue[colorType][color]) {
            presetColors[className] = colorsValue[colorType][color];
          }
        }
      }
      presetColors = { ...presetColors, ...DEFAULT_COLOR };
      return presetColors;
    },
  },
  created() {},
  methods: {
    titleButton(button: any) {
      return DISPLAY_TITLE_BUTTON[button && button.key] || 'Custom Button';
    },
    formatCssStyle(cssKey: any, cssValue: any) {
      return `${cssKey}: ${cssValue} !important`;
    },
    setCssStyle(cssValue: any) {
      return `${cssValue} !important`;
    },
    getBackgroundColorPresetOptions(key: string | number) {
      return this.presetColors[key];
    },
    getValueWithDataUnit(data: string) {
      const result: any = data.match(/[a-z]+|[^a-z]+/gi);
      return {
        value: parseFloat(result[0]),
        unit: result[1],
      };
    },
    convertButtonOptionToCssObject(button: { value: any }, maxScreenPx = 0, scaleType = 'fontSize') {
      if (maxScreenPx === 0) {
        if (scaleType == 'fontSize') {
          const offsetWidth = document.body.offsetWidth;
          if (offsetWidth <= 1300) {
            maxScreenPx = 58;
          } else {
            maxScreenPx = 72;
          }
        } else {
          maxScreenPx = 24;
        }
      }

      let cssObject: any = Object({});
      let cssLines: any = {};
      const cssHoverLines: any = {};
      const buttonValue = button.value;
      const classNameButton = 'button';
      const classNameIcon = 'icon';
      if (buttonValue.dropShadow && buttonValue.dropShadow.enabled) {
        cssLines['boxShadow'] = this.getButtonDropShadow(buttonValue);
      }
      if (buttonValue.backgroundColor) {
        const bgStyle = buttonValue.backgroundColor.origin
          ? this.setCssStyle(buttonValue.backgroundColor.origin)
          : 'unset !important';
        cssLines['backgroundColor'] = bgStyle;
        const bgHover = buttonValue.backgroundColor.hover
          ? this.setCssStyle(buttonValue.backgroundColor.hover)
          : 'unset !important';
        cssHoverLines['backgroundColor'] = bgHover;

        cssLines['backgroundImage'] = 'unset !important';
        cssHoverLines['backgroundImage'] = 'unset !important';
      }

      if (buttonValue.textColor) {
        const clStyle = this.setCssStyle(buttonValue.textColor.origin);
        cssLines['color'] = clStyle;
        const clHover = this.setCssStyle(buttonValue.textColor.hover);
        cssHoverLines['color'] = clHover;
      }

      if (buttonValue.iconColor) {
        const iClStyle = this.setCssStyle(buttonValue.iconColor.origin);
        cssObject[classNameIcon] = {
          color: iClStyle,
        };
        const iClHover = {
          color: this.setCssStyle(buttonValue.iconColor.hover),
        };
        cssObject[`${classNameIcon}Hover`] = iClHover;
      }

      if (buttonValue.cornerStyle) {
        const corner = this.presetConners.find((item: { id: any }) => item.id == buttonValue.cornerStyle);
        if (corner) {
          cssLines['borderRadius'] = this.setCssStyle(corner.value.value);
        }
      }

      if (buttonValue.borderStyle) {
        const border = this.presetBorders.find((item: { id: any }) => item.id == buttonValue.borderStyle);
        if (border) {
          const borderValue = border.value;
          let borderStyle = {};
          borderStyle = {
            borderColor: this.setCssStyle(borderValue.color),
            borderWidth: this.setCssStyle(borderValue.thickness),
            borderStyle: this.setCssStyle(borderValue.style),
          };
          cssLines = { ...cssLines, ...borderStyle };
        }
      }

      const textStyle = buttonValue.textStyle;
      let maxSize: any = {};

      if (textStyle) {
        const keyTxtLarge = textStyle.large;
        const keyTxtMedium = textStyle.medium;
        const keyTxtSmall = textStyle.small;
        if (keyTxtSmall) {
          const textStyle = this.getTextStyleForButtonSize(this.presetTexts, keyTxtSmall);
          if (textStyle) {
            const sizeButton = textStyle.sizeButton;
            cssObject[BUTTON_TYPE_SMALL] = textStyle.data;
            maxSize = this.getObjMaxSizeButton(BUTTON_TYPE_SMALL, scaleType, sizeButton);
            maxSize.baseSizeSmall = sizeButton.baseSize;
            maxSize.fontSizeSmall = sizeButton.fontSizeRem;
          }
        }

        if (keyTxtMedium) {
          const textStyle = this.getTextStyleForButtonSize(this.presetTexts, keyTxtMedium);
          if (textStyle) {
            const sizeButton = textStyle.sizeButton;
            cssObject[BUTTON_TYPE_MEDIUM] = textStyle.data;
            maxSize = this.getObjMaxSizeButton(BUTTON_TYPE_MEDIUM, scaleType, sizeButton, maxSize);
            maxSize.baseSizeMedium = sizeButton.baseSize;
            maxSize.fontSizeMedium = sizeButton.fontSizeRem;
          }
        }

        if (keyTxtLarge) {
          const textStyle = this.getTextStyleForButtonSize(this.presetTexts, keyTxtLarge);
          if (textStyle) {
            const sizeButton = textStyle.sizeButton;
            cssObject[BUTTON_TYPE_LARGE] = textStyle.data;
            maxSize = this.getObjMaxSizeButton(BUTTON_TYPE_LARGE, scaleType, sizeButton, maxSize);
            maxSize.baseSizeLarge = sizeButton.baseSize;
            maxSize.fontSizeLarge = sizeButton.fontSizeRem;
          }
        }
        cssObject = this.handleResizeButton(scaleType, maxSize, maxScreenPx, cssObject);
      }
      cssObject[classNameButton] = cssLines;
      cssObject[`${classNameButton}Hover`] = cssHoverLines;
      return cssObject;
    },
    getButtonDropShadow(buttonValue: any) {
      const radian = parseInt(buttonValue.dropShadow.angle) * (Math.PI / 180);
      const x = Math.cos(radian) * parseFloat(buttonValue.dropShadow.distance);
      const y = Math.sin(radian) * parseFloat(buttonValue.dropShadow.distance);
      const boxShadowValue = `${x}px ${y}px ${buttonValue.dropShadow.blur} ${buttonValue.dropShadow.size} ${
        buttonValue.dropShadow.color || 'transparent'
      }`;
      return this.setCssStyle(boxShadowValue);
    },

    getTextStyleForButtonSize(presetTexts: any, keyText: string) {
      const text = presetTexts.find((item: { key: string }) => item.key == keyText);
      if (text) {
        const textValue = text.value;
        const cssButtonLines: any = {};
        const fontSizeTextValue = textValue.screens?.lg?.fontSize ?? textValue.fontSize;
        const baseSizeValue = this.getValueWithDataUnit(textValue.baseSize).value;
        const parseFontSize = this.getValueWithDataUnit(fontSizeTextValue);
        let fontSizePx = parseFontSize.value;
        let fontSizeRem = parseFontSize.value;
        if (parseFontSize.unit === 'rem') {
          fontSizePx *= baseSizeValue;
        } else {
          fontSizeRem /= baseSizeValue;
        }
        const paddingXNumber = fontSizePx;
        const paddingYNumber = fontSizePx / 2 + baseSizeValue / 4;
        const paddingX = paddingXNumber + 'px';
        const paddingY = paddingYNumber + 'px';
        cssButtonLines['paddingLeft'] = this.setCssStyle(paddingX);
        cssButtonLines['paddingRight'] = this.setCssStyle(paddingX);
        cssButtonLines['paddingTop'] = this.setCssStyle(paddingY);
        cssButtonLines['paddingBottom'] = this.setCssStyle(paddingY);

        const mappingAttrs: any = getPackAttrByKey('textButtonSize', text);
        for (const [key, value] of Object.entries(mappingAttrs)) {
          if (key == 'fontSize') {
            cssButtonLines[key] = this.setCssStyle(fontSizePx + 'px');
          } else {
            cssButtonLines[`${value}`] = this.setCssStyle(textValue[key]);
          }
        }
        const sizeButton = {
          baseSize: baseSizeValue,
          fontSize: fontSizePx,
          fontSizeRem: fontSizeRem,
          paddingX: paddingXNumber,
          paddingY: paddingYNumber,
        };
        return { data: cssButtonLines, sizeButton: sizeButton };
      }
      return false;
    },
    getValueButton() {
      return (this as any).button.value;
    },
    getObjMaxSizeButton(typeButton: any, scaleType: any, sizeButton: any, oldMaxSize: any = {}) {
      let maxSize: any = {};
      if (oldMaxSize && Object.keys(oldMaxSize).length) {
        if (scaleType === 'preview') {
          return oldMaxSize;
        } else {
          if (sizeButton.fontSize > oldMaxSize.fontSize) {
            maxSize.type = typeButton;
            maxSize.fontSize = sizeButton.fontSize;
            maxSize.baseSize = sizeButton.baseSize;
            return maxSize;
          } else {
            return oldMaxSize;
          }
        }
      } else {
        maxSize = {
          type: typeButton,
          fontSize: sizeButton.fontSize,
          fontSizeRem: sizeButton.fontSizeRem,
          baseSize: sizeButton.baseSize,
        };
        if (scaleType === 'preview') {
          maxSize.height = sizeButton.fontSize + sizeButton.paddingY * 2;
          maxSize.paddingX = sizeButton.paddingX;
          maxSize.paddingY = sizeButton.paddingY;
        }
        return maxSize;
      }
    },
    handleResizeButton(scaleType: any, maxSize: any, maxScreenPx: number, cssObject: any) {
      if (scaleType === 'preview') {
        const heightButton = maxSize.height;
        if (heightButton > maxScreenPx) {
          const ratio = maxScreenPx / heightButton;
          const fontSizePx = maxSize.fontSize * ratio;
          const paddingX = maxSize.paddingX * ratio;
          const paddingY = maxSize.paddingY * ratio;
          cssObject[maxSize.type]['fontSize'] = this.setCssStyle(fontSizePx + 'px');
          cssObject[maxSize.type]['paddingLeft'] = this.setCssStyle(paddingX + 'px');
          cssObject[maxSize.type]['paddingRight'] = this.setCssStyle(paddingX + 'px');
          cssObject[maxSize.type]['paddingTop'] = this.setCssStyle(paddingY + 'px');
          cssObject[maxSize.type]['paddingBottom'] = this.setCssStyle(paddingY + 'px');
        }
      } else {
        const fontSize = maxSize.fontSize;
        if (fontSize > maxScreenPx) {
          const ratio = maxScreenPx / fontSize;
          cssObject = this.getNewCssObjectScale(BUTTON_TYPE_SMALL, cssObject, ratio, maxSize.baseSizeSmall);
          cssObject = this.getNewCssObjectScale(BUTTON_TYPE_MEDIUM, cssObject, ratio, maxSize.baseSizeMedium);
          cssObject = this.getNewCssObjectScale(BUTTON_TYPE_LARGE, cssObject, ratio, maxSize.baseSizeLarge);
        }
      }
      return cssObject;
    },
    getNewCssObjectScale(typeButton: string, cssObject: any, ratio: number, baseSize: any): any {
      if (cssObject && Object.keys(cssObject).length && cssObject[typeButton]) {
        // baseSize = baseSize * ratio;
        const fontSizeOld = this.getValueWithDataUnit(cssObject[typeButton]['fontSize']).value;
        const paddingTopOld = this.getValueWithDataUnit(cssObject[typeButton]['paddingTop']).value;
        const paddingRightOld = this.getValueWithDataUnit(cssObject[typeButton]['paddingRight']).value;
        const newFontSize = fontSizeOld * ratio;
        const paddingX = paddingRightOld * ratio;
        const paddingY = paddingTopOld * ratio;
        cssObject[typeButton]['fontSize'] = this.setCssStyle(newFontSize + 'px');
        cssObject[typeButton]['paddingLeft'] = this.setCssStyle(paddingX + 'px');
        cssObject[typeButton]['paddingRight'] = this.setCssStyle(paddingX + 'px');
        cssObject[typeButton]['paddingTop'] = this.setCssStyle(paddingY + 'px');
        cssObject[typeButton]['paddingBottom'] = this.setCssStyle(paddingY + 'px');
      }
      return cssObject;
    },
  },
});
